import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { AuthSplash } from '../AuthSplash';
import { resetStore } from '../../actions';
import { useNotification } from '../../hooks/useNotification';

const LogoutCallback: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { unsubscribeFromNotifications, unregisterServiceWorker } = useNotification();

  useEffect(() => {
    const handleLogoutCleanup = async () => {
      try {
        console.log('🚪 Starting logout cleanup...');

        // Create a timeout to ensure we don't wait forever
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Cleanup timeout')), 15000); // 15 seconds timeout
        });

        // Race between cleanup and timeout
        await Promise.race([
          (async () => {
            // Unsubscribe from notifications with individual timeout
            try {
              const unsubscribePromise = unsubscribeFromNotifications();
              const unsubscribeTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Unsubscribe timeout')), 8000)
              );
              await Promise.race([unsubscribePromise, unsubscribeTimeout]);
              console.log('✅ Successfully unsubscribed from notifications');
            } catch (error) {
              console.warn('⚠️ Failed to unsubscribe from notifications:', error);
            }

            // Unregister service worker with individual timeout
            try {
              const unregisterPromise = unregisterServiceWorker();
              const unregisterTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Unregister timeout')), 5000)
              );
              await Promise.race([unregisterPromise, unregisterTimeout]);
              console.log('✅ Successfully unregistered service worker');
            } catch (error) {
              console.warn('⚠️ Failed to unregister service worker:', error);
            }

            // Clear localStorage
            localStorage.clear();
            console.log('✅ Cleared localStorage');

            // Reset Redux store
            dispatch(resetStore());
            console.log('✅ Reset Redux store');

            // Add a longer delay to ensure all operations complete
            await new Promise((resolve) => setTimeout(resolve, 3000));
          })(),
          timeoutPromise,
        ]);

        console.log('✅ All cleanup operations completed');
      } catch (error) {
        console.error('❌ Error during logout cleanup:', error);
      } finally {
        // Always redirect after cleanup (success or failure)
        console.log('🔄 Redirecting to home page...');

        // Use replace instead of href to prevent back button issues
        // Add a small delay to ensure all operations are truly complete
        setTimeout(() => {
          console.log('🔄 Executing redirect...');
          window.location.replace('/');
        }, 1000);
      }
    };

    handleLogoutCleanup();
  }, [dispatch, unsubscribeFromNotifications, unregisterServiceWorker]);

  return <AuthSplash title={t('logout.loggingOut')} subtitle={t('logout.pleaseWait')} />;
};

export default LogoutCallback;
